import { useRef, useCallback } from 'preact/hooks';
import styles from './ShortCodeInput.module.css';

interface ShortCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  length?: number;
}

export function ShortCodeInput({ value, onChange, error = false, length = 6 }: ShortCodeInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const chars = Array.from({ length }, (_, i) => value[i] || '');

  const focusInput = useCallback((index: number) => {
    if (index >= 0 && index < length) {
      inputsRef.current[index]?.focus();
    }
  }, [length]);

  const handleInput = useCallback((index: number, e: Event) => {
    const input = e.target as HTMLInputElement;
    const char = input.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(-1);

    const newChars = [...chars];
    newChars[index] = char;
    const newValue = newChars.join('').replace(/\s/g, '');
    onChange(newValue);

    if (char && index < length - 1) {
      focusInput(index + 1);
    }
  }, [chars, onChange, length, focusInput]);

  const handleKeyDown = useCallback((index: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!chars[index] && index > 0) {
        e.preventDefault();
        const newChars = [...chars];
        newChars[index - 1] = '';
        onChange(newChars.join('').replace(/\s/g, ''));
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  }, [chars, onChange, length, focusInput]);

  const handlePaste = useCallback((e: ClipboardEvent) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') ?? '')
      .toUpperCase()
      .replace(/[^A-Z2-9]/g, '')
      .slice(0, length);
    onChange(pasted);
    focusInput(Math.min(pasted.length, length - 1));
  }, [onChange, length, focusInput]);

  return (
    <div class={`${styles.container} ${error ? styles.error : ''}`}>
      {chars.map((char, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el; }}
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          maxLength={2}
          value={char}
          onInput={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e as KeyboardEvent)}
          onPaste={handlePaste}
          onFocus={(e) => (e.target as HTMLInputElement).select()}
          class={`${styles.cell} ${char ? styles.filled : ''}`}
          aria-label={`Character ${i + 1} of ${length}`}
          aria-invalid={error}
        />
      ))}
    </div>
  );
}
